/**
 * The camera.
 *
 * Two things make this harder than `getUserMedia` suggests. First, the useful
 * controls — torch, zoom, focus — are optional everywhere and named differently
 * where they exist, so every one of them is probed rather than assumed. Second,
 * a phone that has been backgrounded comes back with a stream whose tracks have
 * quietly ended, and nothing tells the page except that the video goes black;
 * `isLive` is what the app watches to notice.
 */

export type Facing = 'environment' | 'user';

export interface CameraCapabilities {
  torch: boolean;
  zoom: { min: number; max: number; step: number } | null;
  focusDistance: boolean;
  /** True when the browser will honour a tap-to-focus point. */
  pointsOfInterest: boolean;
}

export class CameraError extends Error {
  readonly kind: 'denied' | 'missing' | 'busy' | 'insecure' | 'unknown';
  constructor(kind: CameraError['kind'], message: string) {
    super(message);
    this.name = 'CameraError';
    this.kind = kind;
  }
}

function classify(err: unknown): CameraError {
  if (!window.isSecureContext) {
    return new CameraError(
      'insecure',
      'The camera needs a secure connection. Open this page over https.',
    );
  }
  const name = err instanceof DOMException ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new CameraError('denied', 'Camera access is blocked for this site.');
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new CameraError('missing', 'No camera matching that request was found.');
    case 'NotReadableError':
    case 'AbortError':
      return new CameraError('busy', 'The camera is already in use by another app.');
    default:
      return new CameraError('unknown', 'The camera could not be started.');
  }
}

export class Camera {
  #stream: MediaStream | null = null;
  #video: HTMLVideoElement;
  #facing: Facing = 'environment';
  #deviceId: string | null = null;

  constructor(video: HTMLVideoElement) {
    this.#video = video;
  }

  get facing(): Facing {
    return this.#facing;
  }

  get deviceId(): string | null {
    return this.#deviceId;
  }

  get stream(): MediaStream | null {
    return this.#stream;
  }

  /** True while a track is actually delivering frames. */
  get isLive(): boolean {
    const track = this.#stream?.getVideoTracks()[0];
    return !!track && track.readyState === 'live' && !track.muted;
  }

  get size(): { w: number; h: number } {
    return { w: this.#video.videoWidth, h: this.#video.videoHeight };
  }

  /**
   * @param preferred `'environment'` for a mirror selfie — the point is that the
   *                  back camera is the good one, and the mirror is what makes
   *                  that possible.
   */
  async start(preferred: Facing = this.#facing, deviceId: string | null = null): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new CameraError('missing', 'This browser has no camera support.');
    }
    this.stop();

    const video: MediaTrackConstraints = deviceId
      ? { deviceId: { exact: deviceId } }
      : { facingMode: { ideal: preferred } };
    video.width = { ideal: 1920 };
    video.height = { ideal: 1080 };
    video.frameRate = { ideal: 30 };

    try {
      this.#stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
    } catch (err) {
      // An exact device that has gone away should not strand the user on a
      // dead screen; fall back to whatever camera the phone will give us.
      if (deviceId) {
        this.#stream = await navigator.mediaDevices
          .getUserMedia({ video: { facingMode: { ideal: preferred } }, audio: false })
          .catch((e) => {
            throw classify(e);
          });
      } else {
        throw classify(err);
      }
    }

    this.#facing = preferred;
    const track = this.#stream.getVideoTracks()[0];
    this.#deviceId = track?.getSettings().deviceId ?? null;

    this.#video.srcObject = this.#stream;
    this.#video.setAttribute('playsinline', '');
    await this.#video.play().catch(() => {
      /* Autoplay refusals resolve themselves on the next user gesture. */
    });
    await this.#firstFrame();
  }

  /** Resolve once the element actually knows its dimensions. */
  #firstFrame(): Promise<void> {
    if (this.#video.videoWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        this.#video.removeEventListener('loadedmetadata', done);
        resolve();
      };
      this.#video.addEventListener('loadedmetadata', done);
      // A stream that never fires the event should not block the app forever.
      setTimeout(done, 4000);
    });
  }

  stop(): void {
    this.#stream?.getTracks().forEach((t) => t.stop());
    this.#stream = null;
    this.#video.srcObject = null;
  }

  async devices(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const all = await navigator.mediaDevices.enumerateDevices().catch(() => []);
    return all.filter((d) => d.kind === 'videoinput');
  }

  capabilities(): CameraCapabilities {
    const track = this.#stream?.getVideoTracks()[0];
    const caps = (track?.getCapabilities?.() ?? {}) as MediaTrackCapabilities & {
      torch?: boolean;
      zoom?: { min: number; max: number; step?: number };
      focusDistance?: unknown;
      pointsOfInterest?: unknown;
    };
    return {
      torch: caps.torch === true,
      zoom: caps.zoom ? { min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step ?? 0.1 } : null,
      focusDistance: caps.focusDistance !== undefined,
      pointsOfInterest: caps.pointsOfInterest !== undefined,
    };
  }

  /** Apply an advanced constraint, ignoring the ones this device will not take. */
  async #applyAdvanced(constraint: Record<string, unknown>): Promise<boolean> {
    const track = this.#stream?.getVideoTracks()[0];
    if (!track?.applyConstraints) return false;
    try {
      await track.applyConstraints({ advanced: [constraint] } as MediaTrackConstraints);
      return true;
    } catch {
      return false;
    }
  }

  setTorch(on: boolean): Promise<boolean> {
    return this.#applyAdvanced({ torch: on });
  }

  setZoom(value: number): Promise<boolean> {
    return this.#applyAdvanced({ zoom: value });
  }

  /** Focus where the user tapped, in normalised frame coordinates. */
  focusAt(x: number, y: number): Promise<boolean> {
    return this.#applyAdvanced({
      focusMode: 'single-shot',
      pointsOfInterest: [{ x, y }],
    });
  }

  get zoom(): number {
    const settings = this.#stream?.getVideoTracks()[0]?.getSettings() as
      | (MediaTrackSettings & { zoom?: number })
      | undefined;
    return settings?.zoom ?? 1;
  }
}
